import React, { useState, useEffect } from 'react';
import { Select } from 'tea-component';
import arrowRightBlack from '@sg/assests/svg/arrow-right-black.svg';

interface TagSelectComponentProps {
    value: any;
    record: any;
    editData?: any[];
    stableUpdater?: (value: any) => void;
    fieldKey: string;
    options: Array<{ value: string; text: string }>;
    colorType?: 'dynamic' | 'fixed';
    disabled?: boolean;
    hasError?: boolean;
}

const TagSelectComponent: React.FC<TagSelectComponentProps> = ({
    value,
    record,
    editData,
    stableUpdater,
    fieldKey,
    options,
    colorType = 'dynamic',
    disabled = false,
    hasError = false
}) => {
    const [_hasError, setHasError] = useState(hasError);
    
    const getCurrentValue = () => {
        if (editData && record?.id) {
            const editRecord = editData.find((item: any) => item.id === record.id);
            if (editRecord && editRecord[fieldKey] !== undefined) {
                return editRecord[fieldKey];
            }
        }
        return value || '';
    };

    const [selectedValue, setSelectedValue] = useState(getCurrentValue());

    // Sync with editData changes (for auto-save restore) and record.id changes
    useEffect(() => {
        setSelectedValue(getCurrentValue());
    }, [record?.id, editData]);

    // 同步外部 hasError 的变化
    useEffect(() => {
        setHasError(hasError);
    }, [hasError]);

    const handleChange = (newValue: any) => {
        setHasError(false);
        setSelectedValue(newValue);

        // 使用 stableUpdater 传入对象来更新指定字段
        if (stableUpdater && fieldKey) {
            stableUpdater({ [fieldKey]: newValue });
        }
    };

    // 根据 colorType 计算占位文本
    const selectText = colorType === 'fixed' ? 'Select Status' : 'Select Urgency';

    // 根据 colorType 和 selectedValue 确定样式
    const hasSelectedValue = Boolean(selectedValue);
    const getTagStyle = () => {
        if (disabled) {
            // 禁用状态：灰色 + 加粗
            return {
                color: '#6A6D7E',
                backgroundColor: '#2628321A',
                arrowColor: '#262832',
                fontWeight: 'bold'
            };
        }
        if (!hasSelectedValue) {
            return {
                color: '#6A6D7E',
                backgroundColor: '#2628321A',
                arrowColor: '#6A6D7E',
                fontWeight: 'bold'
            };
        }
        if (colorType === 'fixed') {
            // 固定紫色（用于 status）
            return {
                color: '#7F6FF1',
                backgroundColor: '#7F6FF11F',
                arrowColor: '#7F6FF1',
                fontWeight: 'normal'
            };
        } else {
            // 动态颜色（用于 urgency）
            if (selectedValue === 'Yes') {
                return {
                    color: '#EF5858',
                    backgroundColor: '#F17C864D',
                    arrowColor: '#EF5858',
                    fontWeight: 'normal'
                };
            }
            return {
                color: '#29B569',
                backgroundColor: '#69D19C4D',
                arrowColor: '#29B569',
                fontWeight: 'normal'
            };
        }
    };

    const tagStyle = getTagStyle();

    return (
        <Select
            appearance="pure"
            value={selectedValue}
            onChange={handleChange}
            options={options}
            disabled={disabled}
            listHeight={options.length * 36}
            boxClassName="dbs-common-box"
            button={() => {
                return (
                    <div
                        className="dbs-tag-select-button"
                        style={{
                            color: _hasError ? '#EF5858' : tagStyle.color,
                            backgroundColor: _hasError ? '#2628321A' : tagStyle.backgroundColor,
                            padding: '0 8px',
                            height: '18px',
                            lineHeight: '18px',
                            fontSize: '12px',
                            borderRadius: '4px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            cursor: 'pointer',
                            fontWeight: tagStyle.fontWeight
                        }}
                    >
                        <span style={{ fontWeight: 500 }}>{selectedValue || selectText}</span>
                        <span
                            style={{
                                width: '10px',
                                height: '10px',
                                display: 'inline-block',
                                backgroundColor: _hasError ? '#EF5858' : tagStyle.arrowColor,
                                WebkitMask: `url(${arrowRightBlack}) no-repeat center / contain`,
                                mask: `url(${arrowRightBlack}) no-repeat center / contain`,
                                transform: 'rotate(90deg)',
                                pointerEvents: 'none',
                                fontWeight: 700
                            }}
                        />
                    </div>
                );
            }}
        />
    );
};

export default TagSelectComponent;
